import type { ActivityLanguageValue, ParticipationValue, ContentLang } from '@yoyoclub/shared';

/**
 * Recorded Course API Service
 * Buyer/public recorded-course calls: catalog, checkout, purchase status,
 * my courses, and entitled playback tokens.
 */

export type CoursePaymentMethod = 'card' | 'wechat_pay';
export type PurchaseCurrency = 'usd' | 'cny';

export interface LessonTitleInput {
  titleCN: string | null;
  titleEN: string | null;
  titleAutoLang: ContentLang | null;
}

export interface PublicCourse {
  id: string;
  slug: string;
  titleEN: string | null;
  titleCN: string | null;
  descriptionCN: string | null;
  descriptionEN: string | null;
  price: number; // USD cents
  priceCny: number | null; // CNY cents
  category: string;
  coverImage: string | null;
  host: { id: string; displayName: string } | null;
  // Effective audience (the API fills these even before the backfill runs)
  ageMin: number;
  ageMax: number;
  participation: ParticipationValue;
  language: ActivityLanguageValue;
  firstPublishedAt: string | null;
}

export interface CourseLesson {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  order: number;
  price: number; // USD cents (effective: per-lesson override or course default)
  priceCny: number | null; // CNY cents (effective)
  durationSec?: number | null; // Video length once processed; null until ready
  wechat: { currency: PurchaseCurrency; amount: number };
}

export interface CourseOwnership {
  ownsWholeCourse: boolean;
  ownedVideoIds: string[];
}

export interface CourseCheckoutResult {
  // Null when store credit / promo covered the whole USD total (no Stripe);
  // `completed` is then true and the client goes straight to the course.
  checkoutUrl: string | null;
  coursePurchaseId: string;
  currency: PurchaseCurrency;
  amountTotal: number;
  completed?: boolean;
}

export interface CoursePurchaseStatus {
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  owned: boolean;
  currency: PurchaseCurrency;
  amountTotal: number;
  course: {
    activityId: string;
    slug: string;
    /** English-first label for analytics; pages pick a side from titleCN/titleEN. */
    title: string;
    titleCN: string | null;
    titleEN: string | null;
  };
}

export interface MyCourse {
  activityId: string;
  grantedAt: string;
  slug: string;
  titleEN: string | null;
  titleCN: string | null;
  coverImage: string | null;
}

export interface PlaybackVideo {
  videoId: string;
  titleCN: string | null;
  titleEN: string | null;
  order: number;
  durationSec: number | null;
  playbackId: string;
  token: string;
}

// ─── Host/admin sales reporting ────────────────────────────────────
// Completed purchases only. Money fields are minor units of `currency`;
// `storeCredit` and `netCharged` are already net of any partial refund.

export interface CourseSalesTotals {
  currency: PurchaseCurrency;
  subtotal: number;
  /** Discount the host bears (platform promo codes). */
  discount: number;
  /** Member new user promo code discount, paid by the platform (docs/prd-member-rewards.md §3.6). */
  memberDiscount: number;
  storeCredit: number;
  netCharged: number;
}

export interface CourseSalesLesson {
  order: number;
  title: string;
  perLessonUnits: number;
  wholeCourseUnits: number;
  /**
   * What buyers actually paid for this lesson in per-lesson checkouts (post
   * discount and post refund), plus any member new user promo code discount the platform
   * pays. Whole-course purchases are counted in `wholeCourseUnits`, not here.
   */
  revenue: Array<{ currency: PurchaseCurrency; amount: number }>;
}

export interface CourseSalePurchase {
  id: string;
  customerName: string;
  /** Admin only; absent in host responses. */
  customerEmail?: string;
  customerPhone: string;
  purchaseType: 'whole' | 'lesson';
  lessons: string[];
  currency: PurchaseCurrency;
  subtotal: number;
  discount: number;
  memberDiscount: number;
  storeCredit: number;
  netCharged: number;
  paymentMethod: CoursePaymentMethod;
  completedAt: string;
  refundNote: string;
}

export interface CourseSalesReport {
  course: { id: string; titleCN: string | null; lessonCount: number };
  summary: {
    purchaseCount: number;
    buyerCount: number;
    wholeCourseCount: number;
    perLessonCount: number;
    partialRefundCount: number;
    totals: CourseSalesTotals[];
  };
  lessons: CourseSalesLesson[];
  purchases: CourseSalePurchase[];
}

export interface CourseSalesExportFilters {
  startDate?: string;
  endDate?: string;
  activityId?: string;
  videoId?: string;
}

// Admin/host preview (includes pending/non-ready segments; token may be null).
export interface PreviewVideo {
  videoId: string;
  titleCN: string | null;
  titleEN: string | null;
  order: number;
  status: 'importing' | 'processing' | 'ready' | 'failed';
  reviewStatus: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
  playbackId: string | null;
  token: string | null;
}

export interface HostCourseSummary {
  id: string;
  slug: string;
  titleEN: string | null;
  titleCN: string | null;
  status: string;
  price: number;
  priceCny: number | null;
  coverImage: string | null;
  lessonCount: number;
  soldCount: number;
}

export interface HostCourse {
  id: string;
  slug: string;
  titleEN: string | null;
  titleCN: string | null;
  titleAutoLang: ContentLang | null;
  descriptionCN: string | null;
  descriptionEN: string | null;
  descriptionAutoLang: ContentLang | null;
  category: string;
  price: number;
  priceCny: number | null;
  status: string;
  ageMin: number;
  ageMax: number;
  images: { imageUrl: string; isCover: boolean }[];
}

export interface HostCourseInput {
  titleCN: string | null;
  titleEN: string | null;
  titleAutoLang: ContentLang | null;
  descriptionCN: string | null;
  descriptionEN: string | null;
  descriptionAutoLang: ContentLang | null;
  category?: string;
  price: number;
  priceCny?: number | null;
  ageMin?: number;
  ageMax?: number;
  images?: { imageUrl: string; isCover?: boolean }[];
  hostId?: string;
}

export interface AdminHostOption {
  id: string;
  displayName: string;
  email: string | null;
}

export interface HostVideo {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  titleAutoLang: ContentLang | null;
  order: number;
  status: 'importing' | 'processing' | 'ready' | 'failed';
  reviewStatus: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
  supersededAt: string | null;
  sourceFileName: string | null;
  // Per-lesson price override (cents); null means inherit the course default.
  price: number | null;
  priceCny: number | null;
}

export interface ReviewQueueItem {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  titleAutoLang: ContentLang | null;
  status: string;
  reviewStatus: string;
  rejectionReason: string | null;
  createdAt: string;
  activity: {
    id: string;
    titleEN: string | null;
    titleCN: string | null;
    titleAutoLang: ContentLang | null;
    slug: string;
    host: { displayName: string } | null;
  };
}

class CourseApi {
  private apiUrl: string;

  constructor() {
    this.apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
  }

  private async extractError(response: Response, fallback: string): Promise<string> {
    try {
      const payload = await response.json();
      if (payload && typeof payload === 'object') {
        const body = payload as { error?: unknown; message?: unknown };
        if (typeof body.error === 'string' && body.error.trim()) return body.error;
        if (typeof body.message === 'string' && body.message.trim()) return body.message;
      }
    } catch {
      // non-JSON; fall through
    }
    return fallback;
  }

  private authHeaders(token: string) {
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  }

  /** Course detail by slug (reuses the public activity-by-slug endpoint). */
  async getCourseBySlug(slug: string): Promise<{
    id: string;
    slug: string;
    titleEN: string | null;
    titleCN: string | null;
    descriptionCN?: string | null;
    descriptionEN?: string | null;
    price: number;
    priceCny: number | null;
    category?: string;
    ageMin?: number;
    ageMax?: number;
    // Activity status; non-live (not APPROVED/ACTIVE) means an unlisted preview.
    status?: string;
    // True only when live AND every lesson is ready+approved (checkout gate).
    purchasable?: boolean;
    activityType?: string;
    // What WeChat Pay will actually be charged (resolved by the backend).
    wechat?: { currency: PurchaseCurrency; amount: number };
    // Per-lesson cart list (each with its effective price + WeChat amount).
    lessons?: CourseLesson[];
    images: Array<{ imageUrl: string; isCover: boolean }>;
    host?: {
      id: string;
      displayName: string;
      avatarUrl?: string | null;
      bioZh?: string | null;
      bioEn?: string | null;
    };
  }> {
    // Course-specific, unlisted endpoint: returns a RECORDED course by slug in
    // any status (for preview); the `status`/`purchasable` fields tell the page
    // whether to show the preview banner and allow buying.
    const res = await fetch(`${this.apiUrl}/activities/recorded-courses/${slug}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load course'));
    return (await res.json()).data;
  }

  /** Public recorded-course catalog. */
  async listCourses(): Promise<PublicCourse[]> {
    const res = await fetch(`${this.apiUrl}/activities/recorded-courses`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load courses'));
    return (await res.json()).data || [];
  }

  /**
   * Start a course checkout. Returns the Stripe checkout URL to redirect to.
   * Pass videoIds to buy a selected set of lessons (cart); omit for the
   * (default-off) whole-course purchase.
   */
  async createCheckout(
    activityId: string,
    paymentMethod: CoursePaymentMethod,
    token: string,
    videoIds?: string[],
    locale?: string,
    // One platform promo code plus one member new user promo code (member rewards PRD §3.2)
    promotionCodes: string[] = []
  ): Promise<CourseCheckoutResult> {
    const res = await fetch(`${this.apiUrl}/courses/${activityId}/checkout`, {
      method: 'POST',
      headers: this.authHeaders(token),
      body: JSON.stringify({
        paymentMethod,
        ...(videoIds ? { videoIds } : {}),
        ...(locale ? { locale } : {}),
        ...(promotionCodes.length > 0 ? { promotionCodes } : {}),
      }),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to start checkout'));
    return (await res.json()).data;
  }

  /** Validate a promo code against this course (USD checkout only). */
  async validatePromo(
    activityId: string,
    code: string,
    token: string
  ): Promise<{
    valid: boolean;
    promotionCodeId?: string;
    discountType?: 'FIXED_AMOUNT' | 'PERCENTAGE';
    discountValue?: number;
    reason?: string;
    message?: string;
    isMemberCode?: boolean;
  }> {
    const res = await fetch(`${this.apiUrl}/courses/${activityId}/validate-promo`, {
      method: 'POST',
      headers: this.authHeaders(token),
      body: JSON.stringify({ code }),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to validate code'));
    return (await res.json()).data;
  }

  /** Course-specific purchase status (by Stripe checkout session id). */
  async getPurchaseStatus(sessionId: string, token: string): Promise<CoursePurchaseStatus> {
    const res = await fetch(
      `${this.apiUrl}/courses/purchase-status?session_id=${encodeURIComponent(sessionId)}`,
      { headers: this.authHeaders(token) }
    );
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load purchase status'));
    return (await res.json()).data;
  }

  /** Which lessons of a course the signed-in user already owns. */
  async getCourseOwnership(activityId: string, token: string): Promise<CourseOwnership> {
    const res = await fetch(`${this.apiUrl}/courses/${activityId}/ownership`, {
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load ownership'));
    return (await res.json()).data;
  }

  /** The current user's owned courses. */
  async listMyCourses(token: string): Promise<MyCourse[]> {
    const res = await fetch(`${this.apiUrl}/courses/my-courses`, {
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load your courses'));
    return (await res.json()).data || [];
  }

  /** Signed playback tokens for an entitled course (throws 403 if not owned). */
  async getPlayback(activityId: string, token: string): Promise<PlaybackVideo[]> {
    const res = await fetch(`${this.apiUrl}/activities/${activityId}/playback`, {
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load playback'));
    return (await res.json()).data.videos || [];
  }

  /**
   * Entitled playback resolved by slug (for the watch page). Works even when the
   * course is no longer publicly listed, as long as the user owns it.
   */
  async getPlaybackBySlug(
    slug: string,
    token: string
  ): Promise<{ titleEN: string | null; titleCN: string | null; videos: PlaybackVideo[] }> {
    const res = await fetch(`${this.apiUrl}/courses/${slug}/playback`, {
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load playback'));
    const data = (await res.json()).data;
    return { titleEN: data.titleEN ?? '', titleCN: data.titleCN ?? '', videos: data.videos || [] };
  }

  // ─── Admin / host management ──────────────────────────────────────

  /** Admin: list videos in the review queue (default pending). */
  async adminListReviewQueue(token: string, reviewStatus = 'pending'): Promise<ReviewQueueItem[]> {
    const res = await fetch(
      `${this.apiUrl}/admin/recorded-videos?reviewStatus=${encodeURIComponent(reviewStatus)}`,
      { headers: this.authHeaders(token) }
    );
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load review queue'));
    return (await res.json()).data || [];
  }

  async adminApproveVideo(videoId: string, token: string): Promise<void> {
    const res = await fetch(`${this.apiUrl}/admin/recorded-videos/${videoId}/approve`, {
      method: 'POST',
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to approve video'));
  }

  async adminRejectVideo(videoId: string, reason: string, token: string): Promise<void> {
    const res = await fetch(`${this.apiUrl}/admin/recorded-videos/${videoId}/reject`, {
      method: 'POST',
      headers: this.authHeaders(token),
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to reject video'));
  }

  /** Admin/host preview tokens for all current segments of an activity. */
  async getPreviewPlayback(activityId: string, token: string): Promise<PreviewVideo[]> {
    const res = await fetch(`${this.apiUrl}/activities/${activityId}/preview-playback`, {
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load preview'));
    return (await res.json()).data.videos || [];
  }

  // ─── Host: course + video management ──────────────────────────────

  async hostListCourses(token: string): Promise<HostCourseSummary[]> {
    const res = await fetch(`${this.apiUrl}/host/courses`, { headers: this.authHeaders(token) });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load your courses'));
    return (await res.json()).data || [];
  }

  /** Delete a course (host owns it; admin bypasses). Blocked if it has buyers. */
  async hostDeleteCourse(activityId: string, token: string): Promise<void> {
    const res = await fetch(`${this.apiUrl}/host/activities/${activityId}`, {
      method: 'DELETE',
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to delete course'));
  }

  async hostGetCourse(activityId: string, token: string): Promise<HostCourse> {
    const res = await fetch(`${this.apiUrl}/host/courses/${activityId}`, {
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load course'));
    return (await res.json()).data;
  }

  // Admins create courses on behalf of a host; list selectable hosts.
  async adminListHosts(token: string): Promise<AdminHostOption[]> {
    const res = await fetch(`${this.apiUrl}/admin/hosts`, {
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load hosts'));
    const data = (await res.json()).data as Array<{
      id: string;
      displayName: string;
      user?: { email?: string | null } | null;
    }>;
    return data.map((h) => ({
      id: h.id,
      displayName: h.displayName,
      email: h.user?.email ?? null,
    }));
  }

  async hostCreateCourse(body: HostCourseInput, token: string): Promise<{ id: string }> {
    const res = await fetch(`${this.apiUrl}/host/courses`, {
      method: 'POST',
      headers: this.authHeaders(token),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to create course'));
    return (await res.json()).data;
  }

  async hostUpdateCourse(
    activityId: string,
    body: HostCourseInput,
    token: string
  ): Promise<{ id: string }> {
    const res = await fetch(`${this.apiUrl}/host/courses/${activityId}`, {
      method: 'PUT',
      headers: this.authHeaders(token),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to update course'));
    return (await res.json()).data;
  }

  async hostPublishCourse(activityId: string, token: string): Promise<{ status: string }> {
    const res = await fetch(`${this.apiUrl}/host/courses/${activityId}/publish`, {
      method: 'POST',
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to publish course'));
    return (await res.json()).data;
  }

  async hostUnpublishCourse(activityId: string, token: string): Promise<{ status: string }> {
    const res = await fetch(`${this.apiUrl}/host/courses/${activityId}/unpublish`, {
      method: 'POST',
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to unpublish course'));
    return (await res.json()).data;
  }

  async hostWithdrawCourse(activityId: string, token: string): Promise<{ status: string }> {
    const res = await fetch(`${this.apiUrl}/host/courses/${activityId}/withdraw`, {
      method: 'POST',
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to withdraw course'));
    return (await res.json()).data;
  }

  // Upload a course detail-page image to S3 (public/CDN) and return its URL.
  // Lesson videos use the separate multipart -> Mux pipeline (uploadVideoFile).
  async uploadCourseImage(file: File, token: string): Promise<string> {
    const presign = await fetch(`${this.apiUrl}/upload/presigned-url`, {
      method: 'POST',
      headers: this.authHeaders(token),
      body: JSON.stringify({ fileName: file.name, fileType: file.type, folder: 'courses' }),
    });
    if (!presign.ok) throw new Error(await this.extractError(presign, 'Failed to get upload URL'));
    const { uploadUrl, fileUrl } = (await presign.json()).data as {
      uploadUrl: string;
      fileUrl: string;
    };
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!put.ok) throw new Error('Failed to upload image');
    return fileUrl;
  }

  async hostListVideos(
    activityId: string,
    token: string
  ): Promise<{ videos: HostVideo[]; playability: { canActivate: boolean } }> {
    const res = await fetch(`${this.apiUrl}/host/activities/${activityId}/videos`, {
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load videos'));
    return (await res.json()).data;
  }

  /** Sales report for one course (editor modal). Admin sees buyer emails. */
  async hostCoursePurchases(activityId: string, token: string): Promise<CourseSalesReport> {
    const res = await fetch(`${this.apiUrl}/host/courses/${activityId}/purchases`, {
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to load course sales'));
    return (await res.json()).data;
  }

  /** Course sales as a CSV blob, plus the server-suggested filename. */
  async hostExportCourseSales(
    filters: CourseSalesExportFilters,
    token: string
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams();
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.activityId) params.set('activityId', filters.activityId);
    if (filters.videoId) params.set('videoId', filters.videoId);
    const qs = params.toString();

    const res = await fetch(`${this.apiUrl}/host/courses/export${qs ? `?${qs}` : ''}`, {
      headers: this.authHeaders(token),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to export course sales'));

    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const localDate = new Date().toLocaleDateString('en-CA');
    return { blob: await res.blob(), filename: match?.[1] || `course-sales-${localDate}.csv` };
  }

  async hostCreateVideoSlot(
    activityId: string,
    title: LessonTitleInput,
    order: number,
    token: string
  ): Promise<HostVideo> {
    const res = await fetch(`${this.apiUrl}/host/activities/${activityId}/videos`, {
      method: 'POST',
      headers: this.authHeaders(token),
      body: JSON.stringify({ ...title, order }),
    });
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to add video'));
    return (await res.json()).data;
  }

  /** Rename a lesson (both languages plus the machine-translation marker). */
  async hostUpdateVideoTitle(
    activityId: string,
    videoId: string,
    title: LessonTitleInput,
    token: string
  ): Promise<HostVideo> {
    const res = await fetch(
      `${this.apiUrl}/host/activities/${activityId}/videos/${videoId}/title`,
      {
        method: 'PATCH',
        headers: this.authHeaders(token),
        body: JSON.stringify(title),
      }
    );
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to rename lesson'));
    return (await res.json()).data;
  }

  /**
   * Set a lesson's per-lesson price override (cents). Pass null to clear an
   * override so the lesson inherits the course default price.
   */
  async hostUpdateVideoPrice(
    activityId: string,
    videoId: string,
    body: { price?: number | null; priceCny?: number | null },
    token: string
  ): Promise<HostVideo> {
    const res = await fetch(
      `${this.apiUrl}/host/activities/${activityId}/videos/${videoId}/price`,
      {
        method: 'PATCH',
        headers: this.authHeaders(token),
        body: JSON.stringify(body),
      }
    );
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to update lesson price'));
    return (await res.json()).data;
  }

  async hostCreateReplacement(
    activityId: string,
    videoId: string,
    token: string
  ): Promise<HostVideo> {
    const res = await fetch(
      `${this.apiUrl}/host/activities/${activityId}/videos/${videoId}/replace`,
      { method: 'POST', headers: this.authHeaders(token) }
    );
    if (!res.ok) throw new Error(await this.extractError(res, 'Failed to start replacement'));
    return (await res.json()).data;
  }

  /**
   * Upload a video file to S3 via multipart presigned URLs, then finalize so the
   * backend archives + imports it to Mux. Calls onProgress(0..1) as parts upload.
   */
  async uploadVideoFile(
    activityId: string,
    videoId: string,
    file: File,
    token: string,
    onProgress?: (fraction: number) => void
  ): Promise<HostVideo> {
    const PART_SIZE = 50 * 1024 * 1024; // 50MB parts
    const partCount = Math.max(1, Math.ceil(file.size / PART_SIZE));

    // 1) begin: server generates the key + multipart uploadId
    const beginRes = await fetch(
      `${this.apiUrl}/host/activities/${activityId}/videos/${videoId}/upload`,
      {
        method: 'POST',
        headers: this.authHeaders(token),
        body: JSON.stringify({ fileName: file.name, contentType: file.type || 'video/mp4' }),
      }
    );
    if (!beginRes.ok) throw new Error(await this.extractError(beginRes, 'Failed to begin upload'));
    const { key, uploadId } = (await beginRes.json()).data as { key: string; uploadId: string };

    try {
      // 2+3) Sign and upload parts in small batches so presigned URLs are
      // minted just before use and cannot expire mid-upload on large/slow
      // uploads (each part URL has a ~1h TTL).
      const BATCH = 5;
      const completed: Array<{ partNumber: number; eTag: string }> = [];
      for (let from = 1; from <= partCount; from += BATCH) {
        const batchNumbers: number[] = [];
        for (let n = from; n < from + BATCH && n <= partCount; n += 1) batchNumbers.push(n);

        const signRes = await fetch(
          `${this.apiUrl}/host/activities/${activityId}/videos/${videoId}/upload/parts`,
          {
            method: 'POST',
            headers: this.authHeaders(token),
            body: JSON.stringify({ key, uploadId, partNumbers: batchNumbers }),
          }
        );
        if (!signRes.ok) {
          throw new Error(await this.extractError(signRes, 'Failed to presign parts'));
        }
        const { parts: signed } = (await signRes.json()).data as {
          parts: Array<{ partNumber: number; url: string }>;
        };

        for (const part of signed.sort((a, b) => a.partNumber - b.partNumber)) {
          const start = (part.partNumber - 1) * PART_SIZE;
          const blob = file.slice(start, Math.min(start + PART_SIZE, file.size));
          // Retry each part on a transient failure (flaky network, dropped
          // connection, 5xx) before aborting the whole multi-GB upload. The
          // presigned part URL has a ~1h TTL, so retrying within seconds is safe.
          let putRes: Response | undefined;
          for (let attempt = 1; attempt <= 3; attempt += 1) {
            try {
              putRes = await fetch(part.url, { method: 'PUT', body: blob });
              if (putRes.ok) break;
            } catch {
              // Network error (offline / connection reset): fall through to retry.
              putRes = undefined;
            }
            if (attempt < 3) {
              await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** (attempt - 1)));
            }
          }
          if (!putRes || !putRes.ok) {
            throw new Error(
              `Part ${part.partNumber} upload failed after 3 attempts. Check your network connection and try again.`
            );
          }
          const eTag = putRes.headers.get('ETag');
          if (!eTag) {
            throw new Error(
              'S3 did not return an ETag (check bucket CORS exposes the ETag header)'
            );
          }
          completed.push({ partNumber: part.partNumber, eTag });
          onProgress?.(completed.length / partCount);
        }
      }

      // 4) complete: backend verifies size, archives, kicks off Mux import
      const completeRes = await fetch(
        `${this.apiUrl}/host/activities/${activityId}/videos/${videoId}/upload/complete`,
        {
          method: 'POST',
          headers: this.authHeaders(token),
          body: JSON.stringify({ key, uploadId, parts: completed }),
        }
      );
      if (!completeRes.ok) {
        throw new Error(await this.extractError(completeRes, 'Failed to finalize upload'));
      }
      return (await completeRes.json()).data;
    } catch (err) {
      // Best-effort abort so a failed upload does not linger as a partial S3 object.
      await fetch(`${this.apiUrl}/host/activities/${activityId}/videos/${videoId}/upload/abort`, {
        method: 'POST',
        headers: this.authHeaders(token),
        body: JSON.stringify({ key, uploadId }),
      }).catch(() => undefined);
      throw err;
    }
  }
}

export const courseApi = new CourseApi();
